import { useState } from 'react';
import { type FlagConfiguration, useFlagsService } from '@atlassian/jira-flags';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { deletePrioritySchemeTrackEvent } from '../../common/constants';
import type { DeleteSchemeModalProps, PriorityScheme } from '../../common/ui/types';
import { deletePrioritySchemeRequest } from '../../services/delete-priority-scheme';
import type { DeleteSchemeResult } from '../../services/types';
import { messages } from './messages';

export type DeletePrioritySchemeProps = {
	deleteModalProps: DeleteSchemeModalProps;
	setDeleteModalOpen: (priorityScheme: PriorityScheme) => void;
	onSubmitDeleteScheme: () => void;
	onCompleteDeleteScheme: () => void;
};

type OnDeleteSchemeSuccess = () => void;

export const useDeletePrioritySchemeController = (onDeleteSchemeSuccess: OnDeleteSchemeSuccess) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagsService();
	const [deleteModalProps, setDeleteModalProps] = useState<DeleteSchemeModalProps>({
		isOpen: false,
	});

	const showDeleteSchemeSuccessFlag = (schemeId: string, schemeName: string) => {
		const flagConfig: FlagConfiguration = {
			type: 'success',
			id: `priority-scheme-delete-success-flag-${schemeId}`,
			testId: 'admin-pages-priority-schemes-priority-scheme-modal.delete-scheme-flag',
			title: [messages.deleteSchemeFlagTitle, { schemeName }],
			isAutoDismiss: true,
		};

		showFlag(flagConfig);
	};

	const showUnexpectedErrorFlag = () => {
		const flagConfig: FlagConfiguration = {
			type: 'error',
			id: 'priority-scheme-unexpected-error-flag',
			testId: 'admin-pages-priority-schemes-priority-scheme-modal.unexpected-error-flag',
			title: messages.deleteUnexpectedErrorFlagTitle,
			description: messages.deleteUnexpectedErrorFlagDescription,
			isAutoDismiss: true,
		};

		showFlag(flagConfig);
	};

	const setDeleteModalOpen = (priorityScheme: PriorityScheme) => {
		setDeleteModalProps({
			isOpen: true,
			isSubmitting: false,
			schemeId: priorityScheme.id,
			schemeName: priorityScheme.name,
			associatedProjectCount: priorityScheme.projects.total,
		});
	};

	const onCompleteDeleteScheme = () =>
		setDeleteModalProps({
			isOpen: false,
		});

	const onSubmitDeleteScheme = async () => {
		try {
			if (!deleteModalProps.isOpen) {
				showUnexpectedErrorFlag();
				onCompleteDeleteScheme();
				return;
			}

			setDeleteModalProps({
				...deleteModalProps,
				isSubmitting: true,
			});

			fireTrackAnalytics(
				createAnalyticsEvent({}),
				`${deletePrioritySchemeTrackEvent.actionSubject} ${deletePrioritySchemeTrackEvent.action}`,
				deletePrioritySchemeTrackEvent.actionSubjectId,
				{
					prioritySchemeId: deleteModalProps.schemeId,
					prioritySchemeProjectsCount: deleteModalProps.associatedProjectCount,
				},
			);

			const response: DeleteSchemeResult = await deletePrioritySchemeRequest(
				deleteModalProps.schemeId,
			);

			if (!response.isSuccess) {
				showUnexpectedErrorFlag();
			} else {
				onDeleteSchemeSuccess();
				showDeleteSchemeSuccessFlag(deleteModalProps.schemeId, deleteModalProps.schemeName);
			}
		} catch (e) {
			showUnexpectedErrorFlag();
		}

		onCompleteDeleteScheme();
	};

	return {
		deleteModalProps,
		setDeleteModalOpen,
		onSubmitDeleteScheme,
		onCompleteDeleteScheme,
	};
};
